import { useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useUnseenBadges } from "../hooks/useUnseenBadges";
import { useBottomNavItems, type NavIconName } from "../lib/bottomNavItems";

const icons: Record<NavIconName, React.ReactNode> = {
  home: <path d="M4 11.5 12 4l8 7.5M6 10v9a1 1 0 0 0 1 1h3v-6h4v6h3a1 1 0 0 0 1-1v-9" />,
  calendar: (
    <>
      <rect x="4" y="5" width="16" height="16" rx="2" />
      <path d="M8 3v4M16 3v4M4 10h16" strokeLinecap="round" />
    </>
  ),
  list: (
    <>
      <circle cx="4.5" cy="6" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="4.5" cy="12" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="4.5" cy="18" r="1.3" fill="currentColor" stroke="none" />
      <path d="M9 6h11M9 12h11M9 18h11" strokeLinecap="round" />
    </>
  ),
  check: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path d="M8 12.5 11 15.5 16 9" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  star: <path d="M12 2.5 14.8 9l6.7.6-5 4.5 1.5 6.6L12 17.4 6 20.7l1.5-6.6-5-4.5 6.7-.6L12 2.5Z" strokeLinejoin="round" />,
  note: (
    <>
      <rect x="5" y="3" width="14" height="18" rx="2" />
      <path d="M8 8h8M8 12h8M8 16h5" strokeLinecap="round" />
    </>
  ),
  mood: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 10.5h.01M15.5 10.5h.01" strokeLinecap="round" />
      <path d="M8 15c1.2 1.3 2.6 2 4 2s2.8-.7 4-2" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  piggy: (
    <>
      <ellipse cx="12" cy="7" rx="7" ry="3" />
      <path d="M5 7v10c0 1.7 3.1 3 7 3s7-1.3 7-3V7" />
      <path d="M5 12c0 1.7 3.1 3 7 3s7-1.3 7-3" />
    </>
  ),
  link: (
    <>
      <path d="M9 15 15 9" strokeLinecap="round" />
      <path d="M11 6.5 13 4.5a4 4 0 0 1 5.7 5.7l-2 2M13 17.5 11 19.5a4 4 0 0 1-5.7-5.7l2-2" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
};

function Icon({ name }: { name: NavIconName }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
      {icons[name]}
    </svg>
  );
}

export function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const unseen = useUnseenBadges();
  const ITEMS = useBottomNavItems();
  const listRef = useRef<HTMLUListElement>(null);
  // While null, the pill tracks the current route. While dragging, it's a
  // raw 0–1 position that follows the finger continuously (like iOS's tab
  // bar), independent of the 4 discrete tab slots, and only snaps to one on
  // release.
  const [dragRatio, setDragRatio] = useState<number | null>(null);

  const activeIndex = Math.max(
    0,
    ITEMS.findIndex((it) => (it.end ? location.pathname === it.to : location.pathname.startsWith(it.to)))
  );

  function ratioFromClientX(clientX: number) {
    const el = listRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
  }

  // Tracked via document-level listeners rather than pointer capture, so a
  // plain tap still reaches the NavLink underneath and navigates the normal,
  // accessible way — this only kicks in to make the pill visually follow a
  // real drag, snapping to whichever tab it's released over.
  function handlePointerDown(e: React.PointerEvent<HTMLUListElement>) {
    setDragRatio(ratioFromClientX(e.clientX));

    function onMove(ev: PointerEvent) {
      setDragRatio(ratioFromClientX(ev.clientX));
    }
    function onUp(ev: PointerEvent) {
      const ratio = ratioFromClientX(ev.clientX);
      const index = Math.min(ITEMS.length - 1, Math.floor(ratio * ITEMS.length));
      navigate(ITEMS[index].to);
      setDragRatio(null);
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("pointercancel", onCancel);
    }
    function onCancel() {
      setDragRatio(null);
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("pointercancel", onCancel);
    }
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
    document.addEventListener("pointercancel", onCancel);
  }

  const slotWidthPct = 100 / ITEMS.length;
  const pillLeftPct =
    dragRatio !== null
      ? Math.min(100 - slotWidthPct, Math.max(0, dragRatio * 100 - slotWidthPct / 2))
      : activeIndex * slotWidthPct;

  return (
    <nav
      aria-label="Hauptnavigation"
      className="fixed inset-x-0 bottom-0 z-20 px-4 pb-[calc(0.75rem+env(safe-area-inset-bottom))]"
    >
      {/* Fades the page content out behind/beside the floating pill, so
          nothing peeks through the gaps around it — the screen still visually
          "ends" at the bar instead of content showing past its rounded edges. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 -z-10 h-28 bg-gradient-to-t from-cream-50 from-40% to-transparent dark:from-night-bg"
      />
      <ul
        ref={listRef}
        onPointerDown={handlePointerDown}
        className="glass-pill relative z-10 mx-auto flex max-w-md touch-none select-none rounded-full border border-white/60 bg-white/55 shadow-[0_10px_32px_-6px_rgba(43,29,35,0.3)] backdrop-blur-xl backdrop-saturate-150 dark:border-white/10 dark:bg-night-surface/75"
      >
        <span
          aria-hidden="true"
          className={`absolute inset-y-1.5 rounded-full bg-white/80 shadow-[0_2px_10px_rgba(43,29,35,0.12)] dark:bg-night-surface-raised ${
            dragRatio === null ? "transition-[left] duration-300 ease-out" : ""
          }`}
          style={{ width: `calc(${slotWidthPct}% - 6px)`, left: `calc(${pillLeftPct}% + 3px)` }}
        />
        {ITEMS.map((item, index) => (
          <li key={item.to} className="relative z-10 flex-1">
            <NavLink
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex min-h-14 flex-col items-center justify-center gap-0.5 py-2 text-xs font-medium transition-colors ${
                  isActive ? "text-plum-700 dark:text-night-accent" : "text-ink-400 dark:text-night-faint"
                }`
              }
            >
              <span className="relative">
                <Icon name={item.icon} />
                {item.badge && unseen[item.badge] && index !== activeIndex && (
                  <span aria-hidden="true" className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-garnet-600 ring-2 ring-cream-50 dark:ring-night-surface" />
                )}
              </span>
              {item.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
